import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import clsx from 'clsx';
import { useErrorHandler } from 'react-error-boundary';
import styles from './AggregatePlan.module.css';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import '../../cssModules/switch.css';
import { PlanToolBar, BackButton, PlanTable, CsvUploader } from '..';
import { withAllowedOperationsProvider } from '../../hocs';
import {
  exportToExcelAgsn,
  getAggregateSequenceUrl,
  exportToExcelCabAgsn,
  shouldIncludeColourAndCountry
} from '../../utils/utils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { API } from '../../apis/api';
import { buildErrorMessage } from '../../apis/calls';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import OtherPlantPlanUploadErrorsPopup from '../OtherPlantPlanUploadErrorsPopup/OtherPlantPlanUploadErrorsPopup';
import AddEmergencyAgSnForm from '../AddEmergencyAgSnFormForPlan/AddEmergencyAgSnFormForPlan';

const AggregatePlan = ({
  hasActiveEdits,
  setHasActiveEdits,
  aggregate: materialGroupCluster,
  dates,
  sequenceGenerationDate,
  lastRefreshedDate,
  rowsData,
  setCount,
  activeMonthNameWithYear,
  setActiveMonthNameWithYear,
  loadAggregatePlanForAMonth,
  colourOptions,
  countryOptions,
  isGeneratedEnabled,
  isFetchingLastAgsnDate,
}) => {
  const { showPopup, showError } = usePopupManager();
  const history = useHistory();
  const ROW_HEIGHT = 38;
  const ADDITIONAL_DESTINATIONS = ['CKD', 'Spare', 'VFJ'];

  const [plant] = usePlantAndLinesStore().plantState;
  const [plantOptions] = usePlantAndLinesStore().plantOptions;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;

  const [partNumber, setPartNumber] = useState(null);
  const [destination, setDestination] = useState(null);
  const [dateQuantity, setDateQuantity] = useState({});
  const [planHeaders, setPlanHeaders] = useState([]);
  const [lastEmergencyAgSnDate, setLastEmergencyAgSnDate] = useState(null);
  const [highlightPartNumberField, setHighlightPartNumberField] = useState(
    false,
  );
  const [highlightColourField, setHighlightColourField] = useState(
    false,
  );
  const [highlightDestinationField, setHighlightDestinationField] = useState(
    false,
  );
  const [highLightQuantitiesFields, setHighLightQuantitiesFields] = useState(
    {},
  );
  const [isPlanErrorsOpen, setIsPlanErrorsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState();
  const [colorCode, setcolorCode] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [isAddEmergencyAgSnFormOpen, setIsAddEmergencyAgSnFormOpen] = useState(
    false,
  );
  const includeColoursAndCountries = shouldIncludeColourAndCountry(materialGroupCluster);
  
  const handleError = useErrorHandler();

  const fixedColumns = [
    {
      width: 140,
      title: 'Part No.',
      field: 'materialNumber',
      enableSearch: true,
      enableFilter: true,
      headerStyles: { paddingLeft: '2px' },
    },
    {
      width: 235,
      title: 'Part Description',
      field: 'materialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: 'Destination',
      field: 'destination',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 90,
      title: 'Target',
      field: 'target',
    },
    {
      width: 100,
      title: 'Balance',
      field: 'balance',
    },
    ...(includeColoursAndCountries
    ? [
        {
          width: 180,
          title: 'Colour Code',
          field: 'colour',
          enableSearch: true,
          enableFilter: true,
          render: item =>
          item?.colourDescription && item?.colourCode
            ? `${item.colourDescription} (${item.colourCode})`
            : null,
        },
        {
          width: 180,
          title: 'Country Code',
          field: 'country',
          enableSearch: true,
          enableFilter: true,
          render: item =>
          item?.countryCode && item?.country
            ? `${item.countryCode} (${item.country})`
            : null,
        },
      ]
    : []),
  ].map(column => ({
    ...column,
  }));

  const columns = [...fixedColumns];

  const dateColumns = dates.map(date => {
    const field = moment(date).format(DATE_FORMAT.ISO);
    const title = moment(date).format('DD');

    return {
      width: 50,
      field,
      title,
      renderHeader: () => {
        const dateString = field;
        const today = moment().format(DATE_FORMAT.ISO);

        return (
          <div
            className={clsx(
              styles.tablecellTitle,
              styles.tableCellBackground,
              moment(dateString).isSame(today) &&
                clsx(styles.whiteBackground, styles.blueColor),
              moment(dateString).isAfter(today) && styles.blueColor,
            )}
          >
            {title}
          </div>
        );
      },
      render: row => {
        const dateString = field;
        const today = moment().format(DATE_FORMAT.ISO);

        return (
          <div
            className={clsx(
              styles.tablecell,
              row[dateString]?.quantity !== null &&
                moment(dateString).isSame(today) &&
                clsx(styles.whiteBackground, styles.blueColor),
            )}
          >
            {row[dateString] && row[dateString].quantity}
          </div>
        );
      },
    };
  });
  const fetchLastFreezableDate = async (plantId, lineId,dateString) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lastFreezableDate(plantId, lineId,dateString),
      );
      const { data } = response;
      if (data.date) {
        return { status: true, lastFreezableDate: data.date };
      }
    } catch (error) {
      showError({
        contextText: `Failed to Fetch Last Freezable Date`,
        error: error,
      });
    }
  };
  
  const loadFirstEmergencyAgSnDate = async (plant, line) => {
    try {
      if (!plant || !line) return;
      const response = await API.get(API_RESOURCE_URLS.lastAgSNGeneratedDate(plant, line));
      const data = response.data;

      setLastEmergencyAgSnDate(moment(data.date));

    } catch (error) {
      showError({
        contextText: DISPLAY_MESSAGES.AGSN_LAST_DATE_FAILURE,
        info: '',
        error: buildErrorMessage(error),
      });
      setLastEmergencyAgSnDate(null);
    }
  };

  const hasWhiteSpace = string => {
    return /\s/g.test(string);
  };

  const isPartNumberValid = value => {
    if (value && !hasWhiteSpace(value)) {
      setHighlightPartNumberField(false);
      return true;
    }
    setHighlightPartNumberField(true);
    return false;
  };

  const isDestinationValid = value => {
    if (value) {
      setHighlightDestinationField(plant.label === value);
      return plant.label !== value;
    }
    setHighlightDestinationField(true);
    return false;
  };

  const isColourValid = value => {
    if (value) {
      setHighlightColourField(plant.label === value);
      return plant.label !== value;
    }
    setHighlightColourField(true);
    return false;
  };

  const combinedPlantOptions = [
    ...plantOptions,
    ...ADDITIONAL_DESTINATIONS.map(dest => ({ label: dest, value: dest }))
  ];

  const isQuantityValid = (value, field) => {
    if (value >= 0) {
      setHighLightQuantitiesFields(prevState => ({
        ...prevState,
        [field]: false,
      }));
      return true;
    }
    setHighLightQuantitiesFields(prevState => ({
      ...prevState,
      [field]: true,
    }));
    return false;
  };

  const csvDataFormatter = csvData => {
    if (!csvData || csvData.length === 0) return [];      // Get headers from first row
  const headers = Object.keys(csvData[0]);                // Check for invalid headers
  if (!includeColoursAndCountries) {
    const invalidHeaders = headers.filter(header =>
      ['Colour code', 'Country code'].includes(header)
    );
    if (invalidHeaders.length > 0) {
      showError({
        contextText: `CSV contains invalid header(s): ${invalidHeaders.join(
          ', ',
        )}. These are not allowed for non-CAB lines.`,
      });
      return [];
    }}
    const formattedCsvData = csvData.map(data => {
      const formattedRowData = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (key === 'Part No.' || key === 'Destination' || key === 'Colour code' || key === 'Country code')
            return { ...acc, [key]: String(value) };
          if (
            sequenceGenerationDate &&
            moment(key).isSameOrAfter(moment(sequenceGenerationDate))
          )
            return { ...acc, [key]: value ? value : 0 };
          if (!sequenceGenerationDate)
            return { ...acc, [key]: value ? value : 0 };
          return { ...acc };
        },
        {},
      );

      return formattedRowData;
    });

    setPlanHeaders(Object.keys(formattedCsvData[0]));
    return formattedCsvData;
  };

  const updateDateQuantities = (key, value) => {
    setDateQuantity(prev => ({ ...prev, [key]: value }));
  };

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const redirectToAggregateSequence = () => {
    history.push(
      `${getAggregateSequenceUrl(
        materialGroupCluster,
      )}/${sequenceGenerationDate}`,
    );
  };

  const generateSequence = async () => {
    try {
      const formattedDate ={sequenceGenerationDate};
      const freezableDateCheck = await Promise.resolve(
        fetchLastFreezableDate(plant.value, aggregateLine.value,formattedDate['sequenceGenerationDate']),
      );
      if (!freezableDateCheck?.status) return;
      if (
        moment(sequenceGenerationDate).isSameOrBefore(
          moment(freezableDateCheck?.lastFreezableDate),
        )
      ) {
        await API.patch(
          API_RESOURCE_URLS.generateAggregateSequence(
            plant.value,
            aggregateLine.value,
          ),
          {
            date: sequenceGenerationDate,
          },
        );
        redirectToAggregateSequence();
      } else {
        showPopup({
          type: MESSAGE_TYPE.FAILURE,
          error: 'Error',
          contextText:
            DISPLAY_MESSAGES.AGGREGATE_GENERATE_SEQUENCE_DATE_LESS_THEN_FREEZABLE_DATE,
        });
      }
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        error: err,
        contextText: DISPLAY_MESSAGES.AGGREGATE_GENERATE_SEQUENCE,
      });
    }
  };

  const handleOtherPlantUpload = async data => {  
  if (!includeColoursAndCountries) {
    const hasColorData = data.some(item => item.colourCode);
    const hasCountryData = data.some(item => item.countryCode)
    if (hasColorData) {
      showError({
        contextText: `Color code should not be provided for non-CAB lines`,
      });
      return;
    }
    else if (hasCountryData){showError({
        contextText: `country code should not be provided for non-CAB lines`,
      });
      return;

    }
  }
    const payload = data.flatMap(item => {
      const itemDates = Object.keys(item).filter(key => 
          key.match(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/) // matches date format like "4/1/25"
      );
      const dateWiseQuantites = itemDates
          .map(dateStr => {
              const plant = plantOptions.find(
                  plant => plant.label === item.destination,
              );

              const destination = plant ? plant?.value : item.destination;
              const quantity = Number(item[dateStr]);
              const basePayload = {
                materialNumber: item.materialNumber,
                destination,
                date: moment(dateStr, 'M/D/YY').format(DATE_FORMAT.ISO),
                quantity: quantity,
            };
            if (quantity >= 0 && includeColoursAndCountries) {
              return { 
                  ...basePayload, 
                  colourCode: item.colourCode || colorCode ,
                  countryCode:item.countryCode ,
              };
          }
          return basePayload;
          })
          .filter(item => item);
      return dateWiseQuantites;
  });

    try {
      await API.patch(
        API_RESOURCE_URLS.uploadOtherPlantAggregatePlanQuantities(
          plant.value,
          aggregateLine.value,
        ),
        payload,
      );
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.OTHER_PLANT_UPLOAD_PLAN_SUCCESS,
      });
      loadAggregatePlanForAMonth();
    } catch (error) {
      if (error.response?.status === 400) {
        setIsPlanErrorsOpen(true);
        setUploadErrors(error.response.data || []);
      } else if (error.response?.status === 403) {
        setIsPlanErrorsOpen(true);
        setUploadErrors([{ error: DISPLAY_MESSAGES.PLAN_UPLOAD_FORBIDDEN }]);
      } else {
        console.error(buildErrorMessage(error));
        showPopup({
          type: MESSAGE_TYPE.FAILURE,
          error: error,
          contextText: DISPLAY_MESSAGES.OTHER_PLANT_UPLOAD_PLAN_FAILED,
        });
      }
    }
  };

  const handleEmergencyAgSnClick = () => {
    setIsAddEmergencyAgSnFormOpen(true);
  }
  const closeAddEmergencyAgSnForm = () => {
    setIsAddEmergencyAgSnFormOpen(false);
  };

  const handleDownloadTemplateButtonClick = () => {
    const link = document.createElement('a');
    link.setAttribute('download', `Aggregate-plan-template.xlsx`);
    const exportFunction = includeColoursAndCountries 
    ? exportToExcelCabAgsn 
    : exportToExcelAgsn;
    exportFunction().then(dataUri => {
      link.href = dataUri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  useEffect(() => {
    const quantities = {};
    dates.forEach(date => {
      quantities[moment(date).format(DATE_FORMAT.ISO)] = 0;
    });
    setDateQuantity(quantities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFirstEmergencyAgSnDate(plant.value, aggregateLine.value)
  }, [plant, aggregateLine]);

  const isEmergencyAgSnDisabled =
    !lastEmergencyAgSnDate || lastEmergencyAgSnDate.isBefore(moment(), 'day');

  useEffect(() => {
    setHighLightQuantitiesFields(
      dates.reduce(
        (acc, date) => ({
          ...acc,
          [moment(date).format(DATE_FORMAT.ISO)]: false,
        }),
        {},
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates.length]);
  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath={materialGroupCluster}
          action="Plan"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <PlanToolBar
        lastRefreshedDate={lastRefreshedDate}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
        handleMonthSelect={setActiveMonthNameWithYear}
        activeMonthNameWithYear={activeMonthNameWithYear}
      />
      <div className={styles.planTableContainer}>
        <PlanTable
          columns={columns}
          defaultColumns={[...fixedColumns]}
          dateColumns={dateColumns}
          rowHeight={ROW_HEIGHT}
          headerHeight={ROW_HEIGHT}
          rows={rowsData.data}
          setHasActiveEdits={setHasActiveEdits}
          setCount={setCount}
          isDataLoading={rowsData?.loading}
          aggregateSequenceDate={sequenceGenerationDate}
          isEmergencyAgSnDisabled={isEmergencyAgSnDisabled}
          primaryAction={{
            name: "GENERATE SEQUENCE",
            actionFn: generateSequence,
            shouldEnable: () => !isFetchingLastAgsnDate && isGeneratedEnabled,
            authOperation: USER_OPERATIONS.CREATE_AGGREGATE_PLAN_SEQUENCE,
          }}
          secondaryActions={[
            {
              name: "DOWNLOAD TEMPLATE",
              actionFn: () => handleDownloadTemplateButtonClick(),
            },
            {
              name: "Upload",
              authOperation: USER_OPERATIONS.CREATE_AGGREGATE_PLAN_SEQUENCE,
              shouldEnable: () => true,
              component: CsvUploader,
              args: {
                id: "Upload",
                buttonName: "Upload",
                backButtonName: "Aggregate Plan View",
                csvDataFormatter: csvDataFormatter,
                handleSubmit: (data) => handleOtherPlantUpload(data),
                validCsvHeaders: [...planHeaders],
                headerConfiguration: [
                  {
                    title: "partNo.",
                    validationFunction: (values) => isPartNumberValid(values),
                  },
                  {
                    title: "destination",
                    validationFunction: (values) => isDestinationValid(values),
                  },
                  ...(includeColoursAndCountries
                    ? [
                        {
                          title: "colourCode",
                          validationFunction: (values) => isColourValid(values),
                        },
                        {
                          title: "countryCode",
                          validationFunction: (values) => isColourValid(values),
                        },
                      ]
                    : []),
                  ...planHeaders
                    .filter(
                      (item) =>
                        item !== "Part No." &&
                        item !== "Destination" &&
                        item !== "Colour code" &&
                        item !== "Country code"
                    )
                    .map((date) => ({
                      title: date,
                      validationFunction: (values) => isQuantityValid(values),
                    })),
                ],
                headersWithJsonFields: [
                  {
                    csvHeaderName: "Part No.",
                    jsonFieldName: "materialNumber",
                  },
                  {
                    csvHeaderName: "Destination",
                    jsonFieldName: "destination",
                  },
                  ...(includeColoursAndCountries
                    ? [
                        {
                          csvHeaderName: "Colour code",
                          jsonFieldName: "colourCode",
                        },
                        {
                          csvHeaderName: "Country code",
                          jsonFieldName: "countryCode",
                        },
                      ]
                    : []),
                ],
                requiredParams: [
                  {
                    name: "Part No.",
                    placeholder: "Enter Part Number",
                    value: partNumber,
                    setValueFunction: setPartNumber,
                    validationFunction: isPartNumberValid,
                    isFieldValid: true,
                    highlightField: highlightPartNumberField,
                    setHighlightField: setHighlightPartNumberField,
                  },
                  {
                    name: "Destination",
                    placeholder: "Select Destination",
                    value: destination,
                    setValueFunction: setDestination,
                    validationFunction: isDestinationValid,
                    isFieldValid: true,
                    highlightField: highlightDestinationField,
                    setHighlightField: setHighlightDestinationField,
                    requiredOptions: [
                      ...plantOptions.map((plant) => ({
                        ...plant,
                        value: plant.label,
                      })),
                      ...ADDITIONAL_DESTINATIONS.map((destination) => ({
                        label: destination,
                        value: destination,
                      })),
                    ],
                  },
                  ...(includeColoursAndCountries
                    ? [
                        {
                          name: "Colour code",
                          placeholder: "Select Color code",
                          value: colorCode,
                          setValueFunction: setcolorCode,
                          validationFunction: isColourValid,
                          isFieldValid: true,
                          highlightField: highlightColourField,
                          setHighlightField: setHighlightColourField,
                          requiredOptions: colourOptions,
                        },
                        {
                          name: "Country code",
                          placeholder: "Select Country code",
                          value: countryCode,
                          setValueFunction: setCountryCode,
                          validationFunction: isColourValid,
                          isFieldValid: true,
                          highlightField: highlightColourField,
                          setHighlightField: setHighlightColourField,
                          requiredOptions: countryOptions,
                        },
                      ]
                    : []),
                  ...planHeaders
                    .filter(
                      (item) =>
                        item !== "Part No." &&
                        item !== "Destination" &&
                        item !== "Colour code" && 
                        item !== "Country code"
                    )
                    .map((date) => {
                      return {
                        name: date,
                        placeholder: "Enter Quantity",
                        value: dateQuantity[date],
                        setValueFunction: (value) =>
                          updateDateQuantities(date, value),
                        validationFunction: (value) =>
                          isQuantityValid(value, date),
                        isFieldValid: true,
                        highlightField: highLightQuantitiesFields[date],
                        setHighlightField: (value) => {
                          if (!value)
                            setHighLightQuantitiesFields((prevState) => {
                              return Object.keys(prevState).reduce(
                                (acc, key) => {
                                  return { ...acc, [key]: false };
                                },
                                {}
                              );
                            });
                        },
                      };
                    }),
                ],
                addNewRowFormName: "Add Aggregate",
                editRowFormName: "Edit Aggregate",
                addNewRowSuccessMessage:
                  DISPLAY_MESSAGES.PLAN_UPDATED_SUCCESSFUL,
                editRowSuccessMessage: DISPLAY_MESSAGES.PLAN_UPDATED_SUCCESSFUL,
              },
            },
            {
              name: "Add Emergency AgSn",
              authOperation: USER_OPERATIONS.CREATE_AGGREGATE_PLAN_SEQUENCE,
              shouldEnable: () => true,
              actionFn: () => handleEmergencyAgSnClick()
            },
          ]}
        />
      </div>
      <OtherPlantPlanUploadErrorsPopup
        open={isPlanErrorsOpen}
        handleClose={() => setIsPlanErrorsOpen(false)}
        errors={uploadErrors}
      />
      <AddEmergencyAgSnForm
        open={isAddEmergencyAgSnFormOpen}
        handleClose={closeAddEmergencyAgSnForm}
        currentDate={lastRefreshedDate}
        plant={plant.value}
        plantName={plant.label}
        line={aggregateLine.value}
        lineName={aggregateLine.label}
        plantOptions={combinedPlantOptions}
        aggregate={materialGroupCluster}
        loadAggregatePlanForAMonth={loadAggregatePlanForAMonth}
        lastEmergencyAgSnDate={lastEmergencyAgSnDate}
      />
    </>
  );
};

AggregatePlan.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  aggregate: PropTypes.string.isRequired,
  dates: PropTypes.array.isRequired,
  sequenceGenerationDate: PropTypes.string.isRequired,
  lastRefreshedDate: PropTypes.string.isRequired,
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  setCount: PropTypes.func.isRequired,
  setActiveMonthNameWithYear: PropTypes.func.isRequired,
  loadAggregatePlanForAMonth: PropTypes.func,
  colourOptions: PropTypes.array,
  countryOptions: PropTypes.array,
};

export default withAllowedOperationsProvider(
  AggregatePlan,
  true,
  RESOURCE_TYPE.MATERIAL,
);
